import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Play, Activity, TrendingUp, Shield, Zap } from 'lucide-react';
import HeroStatsRibbon from './HeroStatsRibbon';
import { Reveal } from './Reveal';

// Service-themed B-roll loops (verified live). Each represents one of our 4 service lines.
const HERO_SCENES = [
  {
    label: 'Tender Bidding Solutions',
    src: 'https://videos.pexels.com/video-files/6774225/6774225-hd_1920_1080_30fps.mp4',
  },
  {
    label: 'Digital Growth Services',
    src: 'https://videos.pexels.com/video-files/5725960/5725960-hd_1920_1080_30fps.mp4',
  },
  {
    label: 'Website Development',
    src: 'https://videos.pexels.com/video-files/2278095/2278095-hd_1920_1080_30fps.mp4',
  },
  {
    label: 'Customized Business Softwares',
    src: 'https://videos.pexels.com/video-files/3129977/3129977-hd_1920_1080_30fps.mp4',
  },
];
const POSTER_IMG =
  'https://images.pexels.com/photos/48148/document-agreement-documents-sign-48148.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920';

// Live-feed demo entries that cycle in the floating ticker
const ACTIVITY_FEED = [
  { sector: 'Infra EPC', value: '₹11.4 Cr', region: 'Gujarat', status: 'Awarded', tone: 'green' },
  { sector: 'Pharma Procurement', value: '₹85 L', region: 'Maharashtra', status: 'Shortlisted', tone: 'blue' },
  { sector: 'Chemicals — HCL', value: '₹2.3 Cr', region: 'Govt PSU', status: 'Awarded', tone: 'green' },
  { sector: 'Flameproof LED', value: '₹4 Cr', region: 'Railways', status: 'Submitted', tone: 'amber' },
  { sector: 'Water-Treatment Plant', value: '₹64 L', region: 'Rajasthan', status: 'Awarded', tone: 'green' },
  { sector: 'Industrial Fasteners', value: '₹1.2 Cr', region: 'Central PSU', status: 'Negotiation', tone: 'amber' },
];

const toneClass = {
  green: 'text-emerald-400 bg-emerald-500/10 border-emerald-400/30',
  blue: 'text-blue-400 bg-blue-500/10 border-blue-400/30',
  amber: 'text-amber-300 bg-amber-500/10 border-amber-400/30',
};

const Hero = () => {
  const sectionRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [feedIndex, setFeedIndex] = useState(0);
  const [pulseTick, setPulseTick] = useState(0);
  const [sceneIndex, setSceneIndex] = useState(0);

  // Cycle hero background video every 9s with a cross-fade
  useEffect(() => {
    const id = setInterval(() => setSceneIndex((i) => (i + 1) % HERO_SCENES.length), 9000);
    return () => clearInterval(id);
  }, []);

  // Cycle the live-feed every 3.2s
  useEffect(() => {
    const id = setInterval(() => setFeedIndex((i) => (i + 1) % ACTIVITY_FEED.length), 3200);
    return () => clearInterval(id);
  }, []);

  // Pulse ticker every 1.6s so it looks "alive"
  useEffect(() => {
    const id = setInterval(() => setPulseTick((t) => t + 1), 1600);
    return () => clearInterval(id);
  }, []);

  // Mouse-parallax
  const handleMouseMove = (e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    // Normalize to [-1..1]
    const nx = (e.clientX - cx) / (rect.width / 2);
    const ny = (e.clientY - cy) / (rect.height / 2);
    setTilt({ x: Math.max(-1, Math.min(1, nx)), y: Math.max(-1, Math.min(1, ny)) });
  };
  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  const scrollTo = (sel) => {
    const el = document.querySelector(sel);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Parallax transforms
  const headlineStyle = {
    transform: `translate3d(${tilt.x * 10}px, ${tilt.y * 6}px, 0)`,
    transition: 'transform 400ms cubic-bezier(0.16,1,0.3,1)',
    willChange: 'transform',
  };
  const subStyle = {
    transform: `translate3d(${tilt.x * 6}px, ${tilt.y * 4}px, 0)`,
    transition: 'transform 500ms cubic-bezier(0.16,1,0.3,1)',
    willChange: 'transform',
  };
  const cardStyle = {
    transform: `translate3d(${tilt.x * -18}px, ${tilt.y * -12}px, 0)`,
    transition: 'transform 500ms cubic-bezier(0.16,1,0.3,1)',
    willChange: 'transform',
  };

  const active = ACTIVITY_FEED[feedIndex];

  return (
    <section
      id="home"
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative min-h-screen overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background video with poster + overlays - 3 scenes cross-fade */}
      <div className="absolute inset-0 pointer-events-none">
        {HERO_SCENES.map((scene, i) => (
          <video
            key={scene.src}
            autoPlay
            loop
            muted
            playsInline
            preload={i === 0 ? 'auto' : 'metadata'}
            poster={POSTER_IMG}
            aria-hidden="true"
            data-testid={i === 0 ? 'hero-bg-video' : `hero-bg-video-${i}`}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              willChange: 'opacity',
              opacity: sceneIndex === i ? 1 : 0,
              transition: 'opacity 1400ms cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 0,
            }}
          >
            <source src={scene.src} type="video/mp4" />
          </video>
        ))}

        {/* Readability overlays */}
        <div className="absolute inset-0 bg-[#040812]/70" />
        <div className="themed-fade-r absolute inset-0 bg-gradient-to-r from-[#040812] via-[#040812]/80 to-[#040812]/20" />
        <div className="themed-fade-t absolute inset-0 bg-gradient-to-t from-[#070B16] via-transparent to-[#070B16]/50" />

        {/* Scene label — subtle, bottom-right of hero */}
        <div className="hidden md:flex absolute bottom-6 right-6 z-10 items-center gap-2 px-3 py-1.5 rounded-full glass-card border border-white/10 pointer-events-auto">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-slate-200">
            {HERO_SCENES[sceneIndex].label}
          </span>
          <span className="flex items-center gap-1 ml-2">
            {HERO_SCENES.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === sceneIndex ? 'w-5 bg-amber-400' : 'w-1.5 bg-white/30'
                }`}
              />
            ))}
          </span>
        </div>
      </div>

      {/* Aurora blobs */}
      <div
        className="aurora-blob aurora-amber aurora-animate-1"
        style={{ width: 420, height: 420, top: '10%', left: '5%' }}
      />
      <div
        className="aurora-blob aurora-blue aurora-animate-2"
        style={{ width: 360, height: 360, bottom: '15%', left: '20%' }}
      />
      <div
        className="aurora-blob aurora-violet aurora-animate-3"
        style={{ width: 300, height: 300, top: '40%', right: '10%' }}
      />

      {/* Decorative dot pattern (bottom-left) */}
      <div
        className="hidden lg:block absolute left-0 bottom-0 w-[400px] h-[240px] dot-pattern opacity-50 pointer-events-none"
        style={{
          maskImage: 'linear-gradient(to top right, black, transparent)',
          WebkitMaskImage: 'linear-gradient(to top right, black, transparent)',
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-40 pb-24 min-h-screen flex flex-col justify-center">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left — headline block */}
          <div className="lg:col-span-7">
            <Reveal variant="up" delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 pulse-dot" />
                <span className="text-xs tracking-[0.2em] uppercase text-amber-400 font-semibold">
                  Enterprise Tender Management · ISO-Grade Compliance
                </span>
              </div>
            </Reveal>

            <Reveal variant="up" delay={120}>
              <h1
                style={headlineStyle}
                className="font-display font-extrabold tracking-tight leading-[1.02] text-[2.75rem] sm:text-[4rem] lg:text-[5.25rem]"
                data-testid="hero-headline"
              >
                <span className="text-white">Win More Tenders.</span>
                <br />
                <span className="text-gradient-mixed">Grow Enterprise Revenue.</span>
              </h1>
            </Reveal>

            <Reveal variant="up" delay={260}>
              <p
                style={subStyle}
                className="mt-8 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed"
              >
                Arvanta Solutions is the strategic partner procurement heads, CFOs and CEOs trust
                to <span className="text-white font-semibold">win high-value tenders</span> and{' '}
                <span className="text-white font-semibold">scale digital pipelines</span> — with
                NDA-protected engagements, audit-ready documentation, and senior-led delivery
                across 20+ sectors.
              </p>
            </Reveal>

            <Reveal variant="up" delay={400}>
              <div className="mt-10 flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={() => scrollTo('#contact')}
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-2 btn-amber rounded-full px-7 py-4 text-sm sm:text-base font-semibold"
                >
                  Request Executive Briefing
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollTo('#why')}
                  data-testid="hero-cta-secondary"
                  className="group inline-flex items-center gap-2 btn-ghost-dark rounded-full px-7 py-4 text-sm sm:text-base font-semibold"
                >
                  <Play className="w-4 h-4" />
                  Review Our Methodology
                </button>
              </div>
            </Reveal>

            <Reveal variant="up" delay={540}>
              <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-[12px] text-slate-400">
                <span className="inline-flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-emerald-400" /> NDA-protected engagements
                </span>
                <span className="inline-flex items-center gap-2">
                  <Zap className="w-3.5 h-3.5 text-amber-400" /> 48-hr urgent bid turnaround
                </span>
                <span className="inline-flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5 text-blue-400" /> 94% qualified-bid win rate
                </span>
              </div>
            </Reveal>
          </div>

          {/* Right — floating live activity card */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <Reveal variant="scale" delay={320} className="w-full max-w-md">
              <div
                style={cardStyle}
                data-testid="hero-activity-card"
                className="relative glass-card rounded-3xl p-6 lg:p-7 border border-white/10 shadow-2xl shadow-black/40 overflow-hidden"
              >
                {/* Subtle animated gradient border halo */}
                <div className="pointer-events-none absolute -inset-24 bg-gradient-to-tr from-amber-500/10 via-blue-500/10 to-violet-500/10 blur-3xl" />

                {/* Header */}
                <div className="relative flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <span className="relative inline-flex w-2 h-2">
                      <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
                      <span className="relative rounded-full w-2 h-2 bg-emerald-400" />
                    </span>
                    <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-emerald-400">
                      Live · Bid Desk
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-500 font-medium">
                    Updated just now
                  </span>
                </div>

                {/* Primary live tile */}
                <div
                  key={feedIndex}
                  className="relative rounded-2xl bg-white/[0.03] border border-white/10 p-5 mb-4 animate-feed-in"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-1">
                        {active.region}
                      </div>
                      <div className="text-white font-bold text-lg leading-tight">
                        {active.sector}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider border ${toneClass[active.tone]}`}
                    >
                      <Activity className="w-3 h-3" />
                      {active.status}
                    </span>
                  </div>

                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-[11px] text-slate-400">Contract value</div>
                      <div className="text-2xl font-extrabold text-white tracking-tight">
                        {active.value}
                      </div>
                    </div>

                    {/* Mini pulsing bar chart */}
                    <div className="flex items-end gap-1 h-10">
                      {[0.4, 0.65, 0.5, 0.85, 0.7, 1].map((h, i) => (
                        <span
                          key={i}
                          className="w-1.5 rounded-full bg-gradient-to-t from-amber-500 to-amber-300"
                          style={{
                            height: `${Math.max(10, (h + ((pulseTick + i) % 3) * 0.1) * 100)}%`,
                            transition: 'height 500ms ease',
                            opacity: 0.85,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Mini feed list */}
                <ul className="relative space-y-2.5">
                  {[0, 1, 2].map((off) => {
                    const item = ACTIVITY_FEED[(feedIndex + 1 + off) % ACTIVITY_FEED.length];
                    return (
                      <li
                        key={off}
                        className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 bg-white/[0.02] border border-white/5"
                      >
                        <div className="min-w-0">
                          <div className="text-xs text-white font-semibold truncate">
                            {item.sector}
                          </div>
                          <div className="text-[10px] text-slate-500">{item.region}</div>
                        </div>
                        <div className="text-xs font-bold text-amber-300 whitespace-nowrap">
                          {item.value}
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {/* Footer meta */}
                <div className="relative mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
                  <div className="text-[10px] text-slate-500">
                    Rolling 90-day qualified pipeline
                  </div>
                  <div className="text-[11px] font-bold text-white">
                    <span className="text-emerald-400">+18%</span> QoQ
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Bottom stats ribbon — preserved */}
        <div className="mt-16 lg:mt-20">
          <HeroStatsRibbon />
        </div>
      </div>
    </section>
  );
};

export default Hero;
